import { EventEmitter as RawEventEmitter } from 'eventemitter3';

import { EventMap, ObservableEventEmitter, TypedEventEmitter, Unsubscribe } from './type.js';

/**
 * Similar to Node's event emitter, this class allows registering listeners for specific events. In addition to the
 * Node implementation, this class returns functions when registering listeners that can be called to unregister the
 * listener.
 *
 * This class also supports string Typescript typing.
 */
export class EventEmitter<Events extends EventMap> implements ObservableEventEmitter<Events> {
  private emitter: TypedEventEmitter<Events>;

  constructor(emitter?: RawEventEmitter) {
    this.emitter = (emitter ?? new RawEventEmitter()) as TypedEventEmitter<Events>;
  }

  /**
   * Register an event listener.
   *
   * @param event The name of the event.
   * @param callback The function called when the event is emitted.
   * @return A function that unregisters the event listener when it's called.
   */
  on<E extends keyof Events>(event: E, callback: Events[E]): Unsubscribe {
    this.emitter.on(event, callback);
    return () => this.emitter.off(event, callback);
  }

  /**
   * Register an event listener. The listener is automatically removed after the event is emitted for the first time.
   *
   * @param event The name of the event.
   * @param callback The function called when the event is emitted.
   * @return A function that unregisters the event listener when it's called.
   */
  once<E extends keyof Events>(event: E, callback: Events[E]): Unsubscribe {
    this.emitter.once(event, callback);
    return () => this.emitter.off(event, callback);
  }

  /**
   * Remove all listeners for a specific event.
   *
   * @param event The name of the event.
   * @return A function that unregisters the event listener when it's called.
   */
  removeAllListeners<E extends keyof Events>(event: E) {
    this.emitter.removeAllListeners(event);
  }

  /**
   * Emit an event, which causes all listeners registered for that event to be called.
   * @param event The name of the event.
   * @param args The arguments provided to the listeners
   * @return True if the event has listeners, false otherwise.
   */
  emit<E extends keyof Events>(event: E, ...args: Parameters<Events[E]>): boolean {
    return this.emitter.emit(event, ...args);
  }
}
